import * as React from 'react';
import { Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { VatReportData } from 'store/vat/models';
import { TaxReportSubsectionDataResponse } from 'store/vat/models/vatReportData';
import { getRawNumberForCopy, useNumberFormat } from 'utils/numberFormat';

interface UKFormProps {
  data?: VatReportData;
  renderValueCell: (boxNumber: string) => void;
  finalTotals: boolean;
}

export const UKForm: React.FC<UKFormProps> = ({
  data,
  renderValueCell,
  finalTotals,
}) => {
  const { formatNumber } = useNumberFormat();
  const [hints, setHints] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    const selectedSubsections: TaxReportSubsectionDataResponse[] | undefined =
      !finalTotals ? data?.subsections : data?.adjustedSubsections;
    if (data === undefined || data === null) return;

    // Build hints map from subsections
    const hintsMap: Record<string, string> = {};

    selectedSubsections?.forEach((subsection) => {
      const boxNumber = subsection.code?.replace(/^Box\s+/, '').toLowerCase();

      const subsectionNameCell = document.getElementById(
        `subsection${boxNumber}-name`
      );
      const subsectionBoxNameCell = document.getElementById(
        `subsection${boxNumber}-box-name`
      );

      const subsectionValueSpan = document.getElementById(
        `subsection${boxNumber}-value-span`
      );

      if (
        subsectionBoxNameCell !== null &&
        subsectionNameCell !== null &&
        subsectionValueSpan !== null
      ) {
        subsectionNameCell.textContent = subsection.name;
        subsectionBoxNameCell.textContent = subsection.code;

        const textContent: any = subsection.net;
        subsectionValueSpan.textContent = formatNumber(textContent);
        subsectionValueSpan.setAttribute('data-raw-value', getRawNumberForCopy(textContent));
      }

      // Store hint if available
      if (subsection.hint) {
        hintsMap[boxNumber] = subsection.hint;
      }
    });

    setHints(hintsMap);
  }, [data, finalTotals, formatNumber]);

  const renderHintIcon = (boxNumber: string) => {
    const hint = hints[boxNumber];
    if (!hint) return null;

    return null;
  };

  return (
    <table className="vatreporttable table">
      <thead>
        <tr>
          <th>UK VAT Return</th>
          <th></th>
          <th style={{ width: 130, textAlign: 'center' }}>£</th>
        </tr>
      </thead>
      <tbody>
        {/* Box 1 */}
        <tr>
          <td id="subsection1-name">
            VAT due on sales and other outputs
            {renderHintIcon('1')}
          </td>
          <td id="subsection1-box-name" className="box">
            1
          </td>
          <td
            id="subsection1-value"
            className="rightalign value-field"
          >
            {renderValueCell('1')}
          </td>
        </tr>

        {/* Box 2 */}
        <tr>
          <td id="subsection2-name">
            VAT due on intra-community acquisitions of goods made in Northern
            Ireland from EU Member States
            {renderHintIcon('2')}
          </td>
          <td id="subsection2-box-name" className="box">
            2
          </td>
          <td
            id="subsection2-value"
            className="rightalign value-field"
          >
            {renderValueCell('2')}
          </td>
        </tr>

        {/* Box 3 */}
        <tr>
          <td id="subsection3-name">
            Total VAT due (the sum of boxes 1 and 2)
            {renderHintIcon('3')}
          </td>
          <td id="subsection3-box-name" className="box">
            3
          </td>
          <td
            id="subsection3-value"
            className="rightalign value-field"
          >
            {renderValueCell('3')}
          </td>
        </tr>

        {/* Box 4 */}
        <tr>
          <td id="subsection4-name">
            VAT reclaimed on purchases and other inputs (including acquisitions
            from the EU)
            {renderHintIcon('4')}
          </td>
          <td id="subsection4-box-name" className="box">
            4
          </td>
          <td
            id="subsection4-value"
            className="rightalign value-field"
          >
            {renderValueCell('4')}
          </td>
        </tr>

        {/* Box 5 - Highlighted row */}
        <tr className="uk-vat-total-row">
          <td id="subsection5-name" className="uk-vat-total-cell">
            Net VAT to be paid to Customs or reclaimed by you (difference
            between boxes 3 and 4)
            {renderHintIcon('5')}
          </td>
          <td id="subsection5-box-name" className="box">
            5
          </td>
          <td
            id="subsection5-value"
            className="rightalign value-field"
          >
            {renderValueCell('5')}
          </td>
        </tr>

        {/* Box 6 */}
        <tr>
          <td id="subsection6-name">
            Total value of sales and all other outputs excluding any VAT
            {renderHintIcon('6')}
          </td>
          <td id="subsection6-box-name" className="box">
            6
          </td>
          <td
            id="subsection6-value"
            className="rightalign value-field"
          >
            {renderValueCell('6')}
          </td>
        </tr>

        {/* Box 7 */}
        <tr>
          <td id="subsection7-name">
            Total value of purchases and all other inputs excluding any VAT
            {renderHintIcon('7')}
          </td>
          <td id="subsection7-box-name" className="box">
            7
          </td>
          <td
            id="subsection7-value"
            className="rightalign value-field"
          >
            {renderValueCell('7')}
          </td>
        </tr>

        {/* Box 8 */}
        <tr>
          <td id="subsection8-name">
            Total value of intra-community dispatches of goods and related
            costs, excluding any VAT, from Northern Ireland to EU Member States
            {renderHintIcon('8')}
          </td>
          <td id="subsection8-box-name" className="box">
            8
          </td>
          <td
            id="subsection8-value"
            className="rightalign value-field"
          >
            {renderValueCell('8')}
          </td>
        </tr>

        {/* Box 9 */}
        <tr>
          <td id="subsection9-name">
            Total value of intra-community acquisitions of goods and related
            costs, excluding any VAT, made in Northern Ireland from EU Member
            States
            {renderHintIcon('9')}
          </td>
          <td id="subsection9-box-name" className="box">
            9
          </td>
          <td
            id="subsection9-value"
            className="rightalign value-field"
          >
            {renderValueCell('9')}
          </td>
        </tr>
      </tbody>
    </table>
  );
};
